"use client";

import React from "react";
import Link from "next/link";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { VideoPlayIndicator } from "@/components/ui/VideoPlayIndicator";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { formatDateTime } from "@/utilities/formatting/date";
import type { Post, Category } from "@/payload-types";

export interface ArticleBentoGridProps {
    articles: Post[];
    layout: "staggered" | "mosaic";
}

function OverlayCard({ article, href }: { article: Post; href: string }) {
    const category = article.categories?.find((c): c is Category => typeof c !== "number" && !!c);
    const imageUrl = getPostImageUrl(article, "xlarge");
    const date = article.publishedAt ? formatDateTime(new Date(article.publishedAt)) : "";
    const isVideo = article.type === "video";

    return (
        <Link href={href} prefetch={false} className="group relative block w-full h-full overflow-hidden rounded-lg">
            <img
                src={imageUrl}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div
                className="absolute inset-0"
                style={{
                    background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, transparent 70%)",
                }}
            />
            {isVideo && <VideoPlayIndicator />}
            <div className="relative z-10 flex h-full flex-col justify-end p-3 md:p-4">
                {category && (
                    <div className="mb-2">
                        <span className="inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-secondary text-secondary-foreground">
                            {category.title}
                        </span>
                    </div>
                )}
                <h3 className="text-xs md:text-sm font-bold text-white leading-snug line-clamp-3">{article.title}</h3>
                {date && <p className="mt-1 text-[10px] text-white/70">{date}</p>}
            </div>
        </Link>
    );
}

/**
 * Staggered bento: 3 flex columns, each with 2 cards.
 * Small cards = 9:16 portrait. Big cards = fill remaining space.
 * Zigzag: cols 1,3 have small on top; col 2 has small on bottom.
 */
function StaggeredGrid({ articles, nav }: { articles: Post[]; nav: ReturnType<typeof useNavigation> }) {
    const items = articles.slice(0, 8);
    if (items.length === 0) return null;

    // Column definitions: [topCardIndex, bottomCardIndex, topIsSmall]
    // 4 columns with alternating zigzag pattern
    const columns: [number, number, boolean][] = [
        [0, 4, true], // Col 1: small top, big bottom
        [1, 5, false], // Col 2: big top, small bottom
        [2, 6, true], // Col 3: small top, big bottom
        [3, 7, false], // Col 4: big top, small bottom
    ];

    return (
        <>
            {/* Desktop: 4 columns with stagger */}
            <div className="hidden md:flex gap-3">
                {columns.map(([topIdx, bottomIdx, topIsSmall], colIdx) => {
                    if (!items[topIdx] || !items[bottomIdx]) return null;
                    return (
                        <div key={colIdx} className="flex-1 flex flex-col gap-3">
                            <div style={{ aspectRatio: topIsSmall ? "9/16" : "9/12" }}>
                                <OverlayCard article={items[topIdx]} href={nav.post(items[topIdx])} />
                            </div>
                            <div style={{ aspectRatio: topIsSmall ? "9/12" : "9/16" }}>
                                <OverlayCard article={items[bottomIdx]} href={nav.post(items[bottomIdx])} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Mobile: 2 columns */}
            <div className="grid grid-cols-2 gap-2 md:hidden">
                {items.map((article) => (
                    <div key={article.id} style={{ aspectRatio: "9/16" }}>
                        <OverlayCard article={article} href={nav.post(article)} />
                    </div>
                ))}
            </div>
        </>
    );
}

/**
 * Mosaic: alternating columns — tall portrait card, then 2 stacked smaller cards, repeating.
 * Pattern: [large] [small/small] [large] [small/small] ...
 * Consumes all provided articles.
 */
function MosaicGrid({ articles, nav }: { articles: Post[]; nav: ReturnType<typeof useNavigation> }) {
    if (articles.length === 0) return null;

    // Group into columns: single large card, then a pair of stacked small cards, alternating
    // Pattern with 6 articles: [large] [small/small] [large] [small/small]
    const columns: ({ type: "large"; article: Post } | { type: "pair"; articles: Post[] })[] = [];
    const items = articles.slice(0, 6);
    let i = 0;
    while (i < items.length) {
        columns.push({ type: "large", article: items[i] });
        i++;
        if (i < items.length) {
            const pair = items.slice(i, Math.min(i + 2, items.length));
            columns.push({ type: "pair", articles: pair });
            i += pair.length;
        }
    }

    return (
        <>
            {/* Desktop */}
            <div className="hidden md:flex gap-3">
                {columns.map((col, idx) =>
                    col.type === "large" ? (
                        <div key={col.article.id} className="flex-1" style={{ aspectRatio: "9/18" }}>
                            <OverlayCard article={col.article} href={nav.post(col.article)} />
                        </div>
                    ) : (
                        <div key={idx} className="flex-1 flex flex-col gap-3">
                            {col.articles.map((article) => (
                                <div key={article.id} className="flex-1">
                                    <OverlayCard article={article} href={nav.post(article)} />
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {/* Mobile: 2 columns, all portrait */}
            <div className="grid grid-cols-2 gap-2 md:hidden">
                {items.map((article) => (
                    <div key={article.id} style={{ aspectRatio: "9/16" }}>
                        <OverlayCard article={article} href={nav.post(article)} />
                    </div>
                ))}
            </div>
        </>
    );
}

export const ArticleBentoGrid: React.FC<ArticleBentoGridProps> = ({ articles, layout }) => {
    const nav = useNavigation();
    if (articles.length === 0) return null;

    if (layout === "mosaic") {
        return <MosaicGrid articles={articles} nav={nav} />;
    }
    return <StaggeredGrid articles={articles} nav={nav} />;
};
